import React, { useState } from 'react';
import { Loader2, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { usePlayerDevelopment } from '../hooks/usePlayerDevelopment';
import { Recommendation, VideoRecommendation } from '../types';

const CORNER_STYLES: Record<string, { badge: string; border: string; bg: string }> = {
  technical:     { badge: 'bg-red-500',    border: 'border-red-200',    bg: 'bg-red-50' },
  physical:      { badge: 'bg-yellow-500', border: 'border-yellow-200', bg: 'bg-yellow-50' },
  psychological: { badge: 'bg-green-500',  border: 'border-green-200',  bg: 'bg-green-50' },
  social:        { badge: 'bg-blue-500',   border: 'border-blue-200',   bg: 'bg-blue-50' },
};

const CORNER_LABELS: Record<string, string> = {
  technical: 'Technical',
  physical: 'Physical',
  psychological: 'Psychological',
  social: 'Social',
};

function formatAttributeName(attr: string): string {
  return attr.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

/**
 * Embedded YouTube player — only embeddable videos should be in the catalogue.
 * Renders as an iframe with 16:9 aspect ratio and native YouTube controls.
 */
function VideoPlayer({ video }: { video: VideoRecommendation }) {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const embedUrl = `https://www.youtube-nocookie.com/embed/${video.videoId}?rel=0&modestbranding=1&enablejsapi=1&origin=${encodeURIComponent(origin)}`;

  return (
    <div>
      <div className="relative w-full rounded-lg overflow-hidden bg-black" style={{ paddingBottom: '56.25%' }}>
        <iframe
          className="absolute inset-0 w-full h-full"
          src={embedUrl}
          title={video.title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </div>
      <p className="text-xs font-medium text-gray-800 mt-1.5 line-clamp-2">{video.title}</p>
      <p className="text-[10px] text-gray-400">{video.channelName}</p>
    </div>
  );
}

/** Shows one video at a time with prev/next navigation and dot indicators */
function VideoCarousel({ videos }: { videos: VideoRecommendation[] }) {
  const [activeIndex, setActiveIndex] = useState(0);

  if (videos.length === 0) return null;
  if (videos.length === 1) return <VideoPlayer video={videos[0]} />;

  const prev = () => setActiveIndex((i) => (i === 0 ? videos.length - 1 : i - 1));
  const next = () => setActiveIndex((i) => (i === videos.length - 1 ? 0 : i + 1));

  return (
    <div>
      <VideoPlayer video={videos[activeIndex]} />
      <div className="flex items-center justify-between mt-2">
        <button
          type="button"
          onClick={prev}
          className="p-1.5 rounded-full hover:bg-gray-200 transition-colors text-gray-500 focus:outline-none min-h-[32px] min-w-[32px] flex items-center justify-center"
          aria-label="Previous video"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-1.5">
          {videos.map((v, i) => (
            <button
              key={v.videoId}
              type="button"
              onClick={() => setActiveIndex(i)}
              className={`rounded-full transition-all focus:outline-none ${
                i === activeIndex
                  ? 'w-2.5 h-2.5 bg-green-500'
                  : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`Video ${i + 1} of ${videos.length}`}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={next}
          className="p-1.5 rounded-full hover:bg-gray-200 transition-colors text-gray-500 focus:outline-none min-h-[32px] min-w-[32px] flex items-center justify-center"
          aria-label="Next video"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

/** Collapsible section with a toggle header */
function CollapsibleSection({
  title,
  icon,
  defaultOpen = false,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center justify-between w-full py-2 text-left focus:outline-none"
        aria-expanded={open}
      >
        <span className="flex items-center gap-1.5">
          {icon}
          <span className="text-sm font-semibold text-gray-700">{title}</span>
        </span>
        <span className="text-gray-400">
          {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </span>
      </button>
      {open && <div className="mt-1">{children}</div>}
    </div>
  );
}

function FocusAreaCard({ recommendation }: { recommendation: Recommendation }) {
  const style = CORNER_STYLES[recommendation.corner] || CORNER_STYLES.technical;
  const label = CORNER_LABELS[recommendation.corner] || recommendation.corner;

  const hasVideos = recommendation.videos.length > 0;
  const hasTips = recommendation.trainingTips.length > 0;

  return (
    <div className={`rounded-xl border ${style.border} ${style.bg} p-4`}>
      {/* Card header — always visible, not collapsible */}
      <div className="mb-3">
        <span className={`inline-block text-[10px] font-bold uppercase tracking-wider text-white px-2 py-0.5 rounded-full ${style.badge}`}>
          {label}
        </span>
        <p className="font-semibold text-gray-900 mt-2">
          {formatAttributeName(recommendation.attribute)}
        </p>
        <p className="text-amber-600 font-bold text-lg">
          {recommendation.currentScore.toFixed(1)}
          <span className="text-xs text-gray-400 font-normal"> /10</span>
        </p>
        <p className="text-xs text-gray-500 mt-1">{recommendation.targetDescription}</p>
      </div>

      {/* Collapsible video section */}
      {hasVideos && (
        <CollapsibleSection
          title="Recommended Training Videos"
          icon={<Play className="w-3.5 h-3.5 text-gray-500" />}
          defaultOpen={false}
        >
          <VideoCarousel videos={recommendation.videos} />
        </CollapsibleSection>
      )}

      {/* Collapsible tips section */}
      {hasTips && (
        <CollapsibleSection title="Training Tips" defaultOpen={!hasVideos}>
          <ul className={hasVideos ? 'space-y-2' : 'list-disc list-inside space-y-1'}>
            {recommendation.trainingTips.map((tip) => (
              <li key={tip.tipId} className="text-xs text-gray-600">
                {hasVideos ? (
                  <>
                    <p className="font-bold">{tip.title}</p>
                    <p>{tip.description}</p>
                    {tip.exampleDrill && <p className="text-gray-400 mt-0.5 italic">Drill: {tip.exampleDrill}</p>}
                  </>
                ) : (
                  <>
                    <span className="font-medium">{tip.title}</span> — {tip.description}
                    {tip.exampleDrill && <span className="text-gray-400 italic"> Drill: {tip.exampleDrill}</span>}
                  </>
                )}
              </li>
            ))}
          </ul>
        </CollapsibleSection>
      )}

      {/* No content fallback */}
      {!hasVideos && !hasTips && (
        <div className="text-center py-3">
          <p className="text-xs text-gray-400">Training videos and tips for this area are coming soon.</p>
        </div>
      )}
    </div>
  );
}

interface DevelopmentFocusAreasProps {
  playerId: string | undefined;
  teamId: string | undefined;
}

export function DevelopmentFocusAreas({ playerId, teamId }: DevelopmentFocusAreasProps) {
  const { recommendations, loading, error } = usePlayerDevelopment(playerId, teamId);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <span className="text-green-500">🎯</span> Development Focus Areas
        </h2>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400 mr-2" />
          <span className="text-sm text-gray-400">Loading recommendations...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <span className="text-green-500">🎯</span> Development Focus Areas
        </h2>
        <div className="bg-red-50 border border-red-100 rounded-xl p-4 text-center">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!recommendations || recommendations.recommendations.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <span className="text-green-500">🎯</span> Development Focus Areas
        </h2>
        <div className="bg-gray-50 border border-dashed border-gray-200 rounded-xl p-6 text-center">
          <p className="text-sm text-gray-400">
            Complete an assessment to see personalised development recommendations.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
      <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
        <span className="text-green-500">🎯</span> Development Focus Areas
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {recommendations.recommendations.map((rec) => (
          <FocusAreaCard key={`${rec.corner}-${rec.attribute}`} recommendation={rec} />
        ))}
      </div>
    </div>
  );
}
